import type { IntegrationSettings } from '@vy/types';
import { INTEGRATION_PROVIDERS } from '@vy/types';
import { raise, validateNotNullOrUndefined } from '@vy/utils';
import { useMemo } from 'react';
import { useIntegrationSettings } from '../../../../../../../hooks/requests/use-integration-settings';
import type { StripeConfigurationFormValues, SyncDirection } from '../StripeConfigurationFormSchema';
import { SYNC_DIRECTIONS } from '../StripeConfigurationFormSchema';
import { IntegrationsSettings } from '@vy/integrations-types';

type FetchStripeSettingsResult = {
  integrationId: string;
  configuration: StripeConfigurationFormValues;
};

export function useFetchStripeSettings(): FetchStripeSettingsResult {
  const integrationSettingsResponse = useIntegrationSettings(INTEGRATION_PROVIDERS.stripe);
  const stripeIntegrationSettings = integrationSettingsResponse.data;

  validateNotNullOrUndefined(stripeIntegrationSettings, 'stripeIntegrationSettings');

  const { integrationId, integrationSettings } = stripeIntegrationSettings;

  return useMemo(
    () => ({
      integrationId,
      configuration: mapIntegrationSettingsToFormValues(integrationSettings),
    }),
    [integrationId, integrationSettings],
  );
}

function mapIntegrationSettingsToFormValues(
  integrationSettings: IntegrationSettings | IntegrationsSettings,
): StripeConfigurationFormValues {
  if (integrationSettings.integrationProvider !== INTEGRATION_PROVIDERS.stripe) {
    raise(`Invalid integration provider ${integrationSettings.integrationProvider}`);
  }

  return {
    apiToken: integrationSettings.integrationConfiguration.authData.bearerToken,
    syncCustomer: getSyncCustomerDirection(
      integrationSettings.integrationConfiguration.autoExportCreatedCustomers,
      integrationSettings.integrationConfiguration.autoImportCustomers,
    ),
    syncApprovedInvoices: integrationSettings.integrationConfiguration.autoExportInvoices,
    syncPaymentRefIdToErp: integrationSettings.integrationConfiguration.syncPaymentRefIdToErp,
  };
}

function getSyncCustomerDirection(autoExportEnabled: boolean, autoImportEnabled: boolean): SyncDirection {
  switch (true) {
    case autoExportEnabled:
      return SYNC_DIRECTIONS.SYNC_FROM_VAYU;
    case autoImportEnabled:
      return SYNC_DIRECTIONS.SYNC_TO_VAYU;
    default:
      return SYNC_DIRECTIONS.AUTO_SYNC_DISABLED;
  }
}
